import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/date'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import MenuGuideButton from '../components/MenuGuideButton'

const ROLLING_LABELS = {
  signupUsers: '가입(계정 생성)',
  distinctLoginUsers: '로그인(고유 사용자)',
  requestsCreated: '설계의뢰 생성',
  healthReportsCreated: '진료정보분석 리포트',
  fireBuildingLookups: '화재 건축물 조회',
  counselSurveySessionsCreated: '상담 설문 세션',
  notificationKakaoFamily: '알림(카카오·SMS계)',
  pageviewsTotal: '페이지뷰(제품·합)',
  pageviewsWebDesktop: 'PV · 웹 PC',
  pageviewsWebMobile: 'PV · 웹 모바일',
  pageviewsWebUnknown: 'PV · 웹(구분 미상·구버전)',
  pageviewsAppIos: 'PV · 앱 iOS',
  pageviewsAppAndroid: 'PV · 앱 Android',
  pageviewsAppUnknown: 'PV · 앱(OS 미상)',
  membersActiveRatioLoginToday: '활성 회원 대비 오늘 로그인 비율',
}

const PERIOD_OPTIONS = [
  { value: '1', label: '최근 1일' },
  { value: '7', label: '최근 7일' },
  { value: '30', label: '최근 30일' },
]

const PUBLIC_LINK_VIEW_ITEMS = [
  { key: 'publicCard', label: '모바일명함' },
  { key: 'personalLanding', label: '개인홈페이지' },
  { key: 'orgLanding', label: '조직홈페이지' },
  { key: 'categoryLanding', label: '홍보용페이지' },
]

const DB_COLLECTION_ITEMS = [
  { key: 'personalLanding', label: '개인홈페이지' },
  { key: 'orgLanding', label: '조직홈페이지' },
  { key: 'categoryLanding', label: '홍보용페이지' },
]

const TREND_PV_KEYS = [
  'pageviewsTotal',
  'pageviewsWebDesktop',
  'pageviewsWebMobile',
  'pageviewsWebUnknown',
  'pageviewsAppIos',
  'pageviewsAppAndroid',
  'pageviewsAppUnknown',
]

/** 일별 추세 — 비즈니스 지표(usage_daily_kst, PV 제외) */
const TREND_BUSINESS_ORDER = [
  'signupUsers',
  'distinctLoginUsers',
  'requestsCreated',
  'healthReportsCreated',
  'fireBuildingLookups',
  'counselSurveySessionsCreated',
  'notificationKakaoFamily',
  'membersActiveStock',
]

const TREND_SERIES_LABELS = {
  ...ROLLING_LABELS,
  membersActiveStock: '활성 회원(일별 스냅샷)',
}

/** 롤링 카드 — 화면에서 카테고리별로 묶어 표시 */
const ROLLING_GROUPS = [
  {
    id: 'biz',
    title: '가입·로그인·업무',
    hint: '계정·의뢰·분석·알림 등 운영 핵심 지표',
    keys: [
      'signupUsers',
      'distinctLoginUsers',
      'requestsCreated',
      'healthReportsCreated',
      'fireBuildingLookups',
      'counselSurveySessionsCreated',
      'notificationKakaoFamily',
    ],
  },
  {
    id: 'pv',
    title: '페이지뷰(제품·채널)',
    hint: '운영자 메뉴 제외 합계 및 웹/앱·채널별',
    keys: [
      'pageviewsTotal',
      'pageviewsWebDesktop',
      'pageviewsWebMobile',
      'pageviewsWebUnknown',
      'pageviewsAppIos',
      'pageviewsAppAndroid',
      'pageviewsAppUnknown',
    ],
  },
  {
    id: 'misc',
    title: '기타',
    hint: '롤링 1일 구간에서만 의미가 큰 비율',
    keys: ['membersActiveRatioLoginToday'],
  },
]

const TREND_LINE_COLORS = {
  signupUsers: '#0d9488',
  distinctLoginUsers: '#2563eb',
  requestsCreated: '#7c3aed',
  healthReportsCreated: '#db2777',
  fireBuildingLookups: '#ea580c',
  counselSurveySessionsCreated: '#ca8a04',
  notificationKakaoFamily: '#4f46e5',
  pageviewsTotal: '#0891b2',
  pageviewsWebDesktop: '#0e7490',
  pageviewsWebMobile: '#06b6d4',
  pageviewsWebUnknown: '#94a3b8',
  pageviewsAppIos: '#7c3aed',
  pageviewsAppAndroid: '#a855f7',
  pageviewsAppUnknown: '#c4b5fd',
  membersActiveStock: '#64748b',
}

const MENU_KEY_LABELS = {
  MENU_HOME: '홈',
  MENU_USAGE_DASHBOARD: '활용도 대시보드',
  MENU_OPS_COSTS: '운영비용',
  MENU_OPS_STATS: '운영 · 통계',
  MENU_OPS_FORMS: '운영 · 서식',
  MENU_OPS_CLAIMS: '운영 · 클레임',
  MENU_OPS_TEMPLATES: '운영 · 템플릿',
  MENU_OPS_SYSTEM_NOTICES: '운영 · 시스템 공지',
  MENU_OPS_BANNERS: '운영 · 배너',
  MENU_OPS_SUPPORT_GUIDE: '운영 · 이용 가이드',
  MENU_OPS_ALERTS: '운영 · 알림관리',
  MENU_OPS_EXTERNAL_PROGRAMS: '운영 · 버전관리',
  MENU_OPS_KAKAO: '운영 · 카카오톡 관리',
  MENU_OPS_OTHER: '운영 · 기타',
  MENU_SUBSCRIPTIONS: '구독',
  MENU_BILLING: '결제·청구',
  MENU_AUDIT_LOGS: '감사 로그',
  MENU_DATA: '데이터',
  MENU_DESIGN_DATA: '설계지원 DB',
  MENU_FILES: '파일',
  MENU_CATEGORY_LANDING: '홍보용페이지',
  MENU_SALES_IMAGES: '이미지 자료실',
  MENU_SALES_PUBLIC_CARD: '모바일명함',
  MENU_SALES_PUBLIC_LANDING: '개인홈페이지',
  MENU_SALES_ORG_HOMEPAGE: '조직홈페이지',
  MENU_SALES_CONSULTATIONS: 'DB수집관리',
  MENU_SALES_PUBLIC_PAGE_SETTINGS: '공개 설정(명함/홈)',
  MENU_SALES: '영업',
  MENU_PUBLIC_SURVEY: '공개 상담 설문',
  MENU_HEALTH: '진료정보분석',
  MENU_FIRE: '화재보험설계',
  MENU_REQUESTS: '설계관리',
  MENU_NOTICES: '공지',
  MENU_SUPPORT_BULK_SMS: '단체문자발송',
  MENU_SUPPORT_KAKAO_BULK: '카카오톡 단체발송',
  MENU_SUPPORT_SURVEYS: '설문관리',
  MENU_SUPPORT_CUSTOMERS: '고객관리',
  MENU_SUPPORT_INQUIRIES: '고객문의',
  MENU_SUPPORT_CLAIMS: '보험금청구',
  MENU_SUPPORT_ACCOUNT_DELETION: '탈퇴 요청',
  MENU_SUPPORT: '고객지원',
  MENU_WORK_INSURANCE_NEWS: '보험뉴스',
  MENU_WORK_INSURER_NEWSLETTERS: '보험사 소식지',
  MENU_WORK_INSURER_SYSTEMS: '보험사전산',
  MENU_WORK_CALENDAR: '일정관리',
  MENU_WORK_POLICY_LOOKUP: '약관조회',
  MENU_WORK: '업무',
  MENU_ORG_DASHBOARD: '조직 대시보드',
  MENU_REPORTS: '통계',
  MENU_ORGS: '조직관리',
  MENU_USERS: '사용자/담당자',
  MENU_SIGNUPS: '가입 승인',
  MENU_WITHDRAWAL: '탈퇴 승인',
  MENU_AVAILABILITY: '가용 시간',
  MENU_PROFILE: '프로필',
  MENU_COUNTERPARTS: '거래처',
  MENU_DIRECTORY: '디렉터리',
  MENU_CUSTOMERS: '고객',
  MENU_TERMS_LIBRARY: '약관 라이브러리',
  MENU_ORG_BANNERS: '조직 배너',
  MENU_UW_EXCEPTIONS: '예외질환조회',
  MENU_SETTINGS: '설정',
  MENU_USER_TASKS: '할 일',
  MENU_PUBLIC_CARD: '공개 모바일명함',
  MENU_PUBLIC_LANDING: '공개 개인홈페이지',
  MENU_PUBLIC_CATEGORY_LANDING: '공개 홍보용페이지',
  MENU_PUBLIC_CLAIM_SIGNATURE: '공개 청구 서명',
  MENU_PUBLIC_CLAIM_INTAKE: '공개 청구 접수',
  MENU_PUBLIC_HEALTH_AUTH: '진료정보 인증',
  MENU_PUBLIC_REQUEST_SHARE: '공유 설계의뢰',
  MENU_PUBLIC_MARKETING: '마케팅·랜딩',
  MENU_AUTH: '로그인·회원가입',
  MENU_LEGAL: '약관·개인정보',
  MENU_OTHER: '기타',
}

const SUBSCRIPTION_STATUS_LABELS = {
  ACTIVE: '활성',
  TRIALING: '체험',
  TRIAL: '체험',
  PAST_DUE: '연체',
  CANCELED: '취소됨',
  CANCELLED: '취소됨',
  INCOMPLETE: '미완료',
  INCOMPLETE_EXPIRED: '미완료(만료)',
  UNPAID: '미납',
  PAUSED: '일시중지',
  EXPIRED: '만료',
}

const ORG_TYPE_LABELS = {
  GA: 'GA',
  INSURER: '보험사',
  PERSONAL: '개인',
}

const OWNER_LABELS = {
  USER: '개인 계정',
  ORG: '조직 계정',
}

function unwrapData(res) {
  const p = res?.data
  if (p?.data !== undefined) return p.data
  return p ?? null
}

function formatNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('ko-KR')
}

function formatPct(x) {
  if (x == null || Number.isNaN(Number(x))) return '—'
  return `${(Number(x) * 100).toFixed(2)}%`
}

function formatMetricPct(x) {
  if (x == null || Number.isNaN(Number(x))) return '—'
  return `${Number(x).toFixed(2)}%`
}

/** API rollingTrendVsPriorPeriod 의 deltaPct (비율 차이, 소수) */
function formatDeltaPct(deltaPct) {
  if (deltaPct == null || Number.isNaN(Number(deltaPct))) return null
  const pct = Number(deltaPct) * 100
  const rounded = Math.abs(pct) >= 10 ? pct.toFixed(0) : pct.toFixed(1)
  if (pct > 0) return `+${rounded}%`
  if (pct < 0) return `${rounded}%`
  return '0%'
}

/** 전체 페이지뷰(합) 대비 메뉴 점유율 — 분모 0이면 표시 불가 */
function formatMenuSharePct(clicks, totalAll) {
  const c = Number(clicks) || 0
  const t = Number(totalAll) || 0
  if (t <= 0) return '—'
  return `${((c / t) * 100).toFixed(2)}%`
}

/** 메뉴 PV 행의 채널별 건수(구 API 호환: 필드 없으면 0) */
function menuPvNum(row, key) {
  const v = row?.[key]
  if (v == null || Number.isNaN(Number(v))) return 0
  return Number(v)
}

function menuLabel(menuKey) {
  if (!menuKey) return '—'
  return MENU_KEY_LABELS[menuKey] || menuKey
}

function subscriptionStatusLabel(st) {
  if (!st) return '—'
  return SUBSCRIPTION_STATUS_LABELS[st] || st
}

function orgTypeLabel(k) {
  if (!k) return '—'
  return ORG_TYPE_LABELS[k] || k
}

function ownerLabel(owner) {
  return OWNER_LABELS[owner] || owner
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full max-w-xl" />
      </div>
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  )
}

function OpsCollapsibleCard({
  title,
  description,
  action,
  collapsed,
  onToggle,
  children,
  contentClassName = '',
}) {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 space-y-0 sm:flex-row sm:items-start sm:justify-between">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{title}</CardTitle>
            <span className="text-xs font-medium text-muted-foreground">{collapsed ? '펼치기' : '접기'}</span>
          </div>
          {description ? <CardDescription className="mt-1.5">{description}</CardDescription> : null}
        </button>
        {action ? <div className="shrink-0">{action}</div> : null}
      </CardHeader>
      {!collapsed ? (
        <CardContent className={contentClassName}>
          {children}
        </CardContent>
      ) : null}
    </Card>
  )
}

export default function OpsUsageDashboardPage() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState('1')
  const [trendRangeDays, setTrendRangeDays] = useState(30)
  const [trendBizKeys, setTrendBizKeys] = useState(() => [
    'signupUsers',
    'distinctLoginUsers',
    'requestsCreated',
  ])
  const [trendPvKeys, setTrendPvKeys] = useState(() => [
    'pageviewsTotal',
    'pageviewsWebDesktop',
    'pageviewsWebMobile',
    'pageviewsAppIos',
    'pageviewsAppAndroid',
  ])
  const [collapsedSections, setCollapsedSections] = useState({
    memberSnapshot: false,
    rollingMetrics: false,
    publicLinks: false,
    dbCollection: false,
    dailyTrend: false,
    menuClicks: false,
  })

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }, [])

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const res = await api.get('/ops/usage-dashboard/summary')
      setData(unwrapData(res))
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '불러오기에 실패했습니다.')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggleTrendBizKey = useCallback((key) => {
    setTrendBizKeys((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev
        return prev.filter((k) => k !== key)
      }
      return [...prev, key]
    })
  }, [])

  const toggleTrendPvKey = useCallback((key) => {
    setTrendPvKeys((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev
        return prev.filter((k) => k !== key)
      }
      return [...prev, key]
    })
  }, [])

  const chartRows = useMemo(() => {
    const pts = data?.dailyTrendKst?.points || []
    const n = trendRangeDays
    if (!pts.length) return []
    return pts.length <= n ? pts : pts.slice(-n)
  }, [data, trendRangeDays])

  if (loading) {
    return (
      <div className="sgez-shadcn-dashboard bg-background text-foreground">
        <DashboardSkeleton />
      </div>
    )
  }

  if (err) {
    return (
      <div className="sgez-shadcn-dashboard bg-background text-foreground">
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">불러오기 실패</CardTitle>
            <CardDescription>{err}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={load}>
              <RefreshCw className="h-4 w-4" />
              다시 시도
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  const rolling = data?.rolling || {}
  const subs = data?.subscriptionsLatestByStatus || {}
  const byOrgType = data?.membersActiveByOrgType || {}
  const pageviewsByMenu = data?.pageviewsByMenu || {}
  const publicLinkViews = data?.publicLinkViews || {}
  const dbCollectionStats = data?.dbCollectionStats || {}
  const rollingWindow = rolling[period] || {}
  const menuRows = pageviewsByMenu[period] || []
  const publicLinkWindow = publicLinkViews[period] || {}
  const dbCollectionWindow = dbCollectionStats[period] || {}
  const menuClickSumShown = menuRows.reduce((sum, row) => sum + (Number(row.clicks) || 0), 0)
  const pageviewsTotalAll = Number(rollingWindow?.pageviewsTotal)
  const menuShareDenominator =
    Number.isFinite(pageviewsTotalAll) && pageviewsTotalAll > 0 ? pageviewsTotalAll : menuClickSumShown
  const periodLabel = PERIOD_OPTIONS.find((o) => o.value === period)?.label || `최근 ${period}일`
  const timeZoneNote = data?.timeZoneNote || '한국 표준시(KST), 자정 기준 롤링 구간'
  const rollupHealth = data?.rollupHealth || {}
  const rollingTrend = data?.rollingTrendVsPriorPeriod?.[period] || {}

  const showRightAxisBiz =
    trendBizKeys.includes('membersActiveStock') && trendBizKeys.some((k) => k !== 'membersActiveStock')

  return (
    <div className="sgez-shadcn-dashboard space-y-8 bg-background text-foreground">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">운영관리 · 활용도 대시보드</h1>
            <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
            <Badge variant="outline" className="font-normal">
              {timeZoneNote}
            </Badge>
          </div>
          {(rollupHealth.usageDailyKstMaxUpdatedAt || rollupHealth.webPageviewsMaxOccurredAt) && (
            <ul className="mt-2 max-w-2xl list-inside list-disc text-xs text-muted-foreground">
              {rollupHealth.usageDailyKstMaxUpdatedAt && (
                <li>
                  일별 롤업 반영 시각(최신):{' '}
                  <span className="text-foreground">{formatDateTime(rollupHealth.usageDailyKstMaxUpdatedAt)}</span>
                </li>
              )}
              {rollupHealth.webPageviewsMaxOccurredAt && (
                <li>
                  페이지뷰 수집 시각(최신):{' '}
                  <span className="text-foreground">{formatDateTime(rollupHealth.webPageviewsMaxOccurredAt)}</span>
                </li>
              )}
            </ul>
          )}
          {data?.generatedAt && (
            <p className="mt-2 text-xs text-muted-foreground">생성 시각: {formatDateTime(data.generatedAt)}</p>
          )}
        </div>
        <Button variant="outline" size="sm" className="shrink-0" onClick={load}>
          <RefreshCw className="h-4 w-4" />
          새로고침
        </Button>
      </div>

      <OpsCollapsibleCard
        title="회원·구독 스냅샷"
        description="활성 계정, 조직 유형별 분포, 구독(계정당 최신) 상태"
        collapsed={collapsedSections.memberSnapshot}
        onToggle={() => toggleSection('memberSnapshot')}
        contentClassName="space-y-6"
      >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="text-xs font-medium text-muted-foreground">활성 계정</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{formatNum(data?.membersActive)}</p>
            </div>
            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="text-xs font-medium text-muted-foreground">오늘 로그인(고유)</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{formatNum(data?.distinctLoginsToday)}</p>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground">활성 계정 — 조직 유형</p>
              <ul className="divide-y rounded-lg border text-sm">
                {Object.keys(byOrgType).length === 0 && (
                  <li className="px-3 py-2 text-muted-foreground">데이터 없음</li>
                )}
                {Object.entries(byOrgType).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span>{orgTypeLabel(k)}</span>
                    <span className="font-medium tabular-nums">{formatNum(v)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              {['USER', 'ORG'].map((owner) => (
                <div key={owner}>
                  <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground">
                    구독(최신) · {ownerLabel(owner)}
                  </p>
                  <ul className="divide-y rounded-lg border text-sm">
                    {Object.keys(subs[owner] || {}).length === 0 && (
                      <li className="px-3 py-2 text-muted-foreground">데이터 없음</li>
                    )}
                    {Object.entries(subs[owner] || {}).map(([st, n]) => (
                      <li key={`${owner}-${st}`} className="flex items-center justify-between gap-2 px-3 py-2">
                        <span>{subscriptionStatusLabel(st)}</span>
                        <span className="font-medium tabular-nums">{formatNum(n)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
      </OpsCollapsibleCard>

      <OpsCollapsibleCard
        title={`롤링 지표 · ${periodLabel}`}
        description={(
          <>
              {timeZoneNote}
              {rollupHealth.priorPeriodNote && (
                <span className="mt-1 block text-xs">{rollupHealth.priorPeriodNote}</span>
              )}
              {period !== '1' && (
                <span className="mt-1 block text-xs">
                  「활성 회원 대비 오늘 로그인 비율」은 1일 구간에서만 계산됩니다.
                </span>
              )}
          </>
        )}
        collapsed={collapsedSections.rollingMetrics}
        onToggle={() => toggleSection('rollingMetrics')}
        contentClassName="space-y-8"
        action={(
          <div className="flex shrink-0 flex-col gap-1 sm:items-end">
            <label htmlFor="usage-period" className="text-xs text-muted-foreground">
              구간 선택
            </label>
            <select
              id="usage-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-9 min-w-[10rem] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
      >
          {ROLLING_GROUPS.map((group) => (
            <div key={group.id} className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-foreground">{group.title}</p>
                {group.hint ? <p className="text-xs text-muted-foreground">{group.hint}</p> : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.keys.map((key) => {
                  const label = ROLLING_LABELS[key]
                  if (!label) return null
                  const raw = rollingWindow[key]
                  const display =
                    key === 'membersActiveRatioLoginToday' ? formatPct(raw) : formatNum(raw)
                  const trend = rollingTrend[key] || {}
                  const priorRaw = trend.priorValue
                  const priorDisplay =
                    key === 'membersActiveRatioLoginToday' ? formatPct(priorRaw) : formatNum(priorRaw)
                  const deltaStr = formatDeltaPct(trend.deltaPct)
                  const deltaClass =
                    trend.deltaPct == null
                      ? 'text-muted-foreground'
                      : Number(trend.deltaPct) > 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : Number(trend.deltaPct) < 0
                          ? 'text-rose-600 dark:text-rose-400'
                          : 'text-muted-foreground'
                  return (
                    <div key={key} className="rounded-lg border bg-muted/20 p-3">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="mt-1 text-lg font-semibold tabular-nums">{display}</p>
                      <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                        직전 동일 구간: <span className="tabular-nums text-foreground">{priorDisplay}</span>
                        {deltaStr != null && (
                          <>
                            {' · '}
                            <span className={`font-medium tabular-nums ${deltaClass}`}>대비 {deltaStr}</span>
                          </>
                        )}
                        {deltaStr == null && trend.priorValue != null && (
                          <span className="text-muted-foreground"> · 대비 —</span>
                        )}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
      </OpsCollapsibleCard>

      <OpsCollapsibleCard
        title={`공개 링크 방문/조회수 · ${periodLabel}`}
        description="고객이 외부 공유 링크를 열어 공개 페이지 데이터 API가 조회된 횟수입니다. 전화·문자·카카오 등 페이지 내부 CTA 클릭은 포함하지 않습니다."
        collapsed={collapsedSections.publicLinks}
        onToggle={() => toggleSection('publicLinks')}
      >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PUBLIC_LINK_VIEW_ITEMS.map((item) => (
              <div key={item.key} className="rounded-lg border bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground">{item.label}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {formatNum(publicLinkWindow[item.key])}
                </p>
              </div>
            ))}
          </div>
      </OpsCollapsibleCard>

      <OpsCollapsibleCard
        title={`DB 수집 통계 · ${periodLabel}`}
        description="전체 사용자 기준 공개 페이지의 조회, 상담요청, 상담완료 집계입니다. 직접등록 DB는 페이지 전환 지표가 없어 제외됩니다."
        collapsed={collapsedSections.dbCollection}
        onToggle={() => toggleSection('dbCollection')}
      >
          <div className="grid gap-3 lg:grid-cols-3">
            {DB_COLLECTION_ITEMS.map((item) => {
              const metric = dbCollectionWindow[item.key] || {}
              return (
                <div key={item.key} className="rounded-lg border bg-muted/20 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{item.label}</p>
                      <p className="mt-1 text-xs text-muted-foreground">조회 → 요청 → 완료 흐름</p>
                    </div>
                    <Badge variant="outline" className="shrink-0 font-normal">
                      전체
                    </Badge>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-md bg-background/70 p-2">
                      <p className="text-[11px] text-muted-foreground">조회</p>
                      <p className="mt-1 text-lg font-semibold tabular-nums">{formatNum(metric.views)}</p>
                    </div>
                    <div className="rounded-md bg-background/70 p-2">
                      <p className="text-[11px] text-muted-foreground">요청</p>
                      <p className="mt-1 text-lg font-semibold tabular-nums">{formatNum(metric.consultRequests)}</p>
                    </div>
                    <div className="rounded-md bg-background/70 p-2">
                      <p className="text-[11px] text-muted-foreground">완료</p>
                      <p className="mt-1 text-lg font-semibold tabular-nums">{formatNum(metric.consultCompleted)}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-1.5 border-t pt-3 text-xs text-muted-foreground">
                    <div className="flex items-center justify-between gap-2">
                      <span>조회 대비 요청율</span>
                      <span className="font-medium text-foreground tabular-nums">
                        {formatMetricPct(metric.viewToRequestRate)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span>요청 대비 완료율</span>
                      <span className="font-medium text-foreground tabular-nums">
                        {formatMetricPct(metric.requestToCompletedRate)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
      </OpsCollapsibleCard>

      <OpsCollapsibleCard
        title="일별 추세 그래프 (KST)"
        description="일별 롤업 테이블(usage_daily_kst) 기준입니다. 맨 오른쪽 날짜(오늘)는 자정부터 현재까지 부분 구간일 수 있습니다. 비즈니스 지표와 페이지뷰는 별도 차트로 나눴습니다."
        collapsed={collapsedSections.dailyTrend}
        onToggle={() => toggleSection('dailyTrend')}
        contentClassName="space-y-10"
        action={(
          <div className="flex shrink-0 gap-1 rounded-md border bg-muted/40 p-1">
            <button
              type="button"
              onClick={() => setTrendRangeDays(14)}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                trendRangeDays === 14 ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              14일
            </button>
            <button
              type="button"
              onClick={() => setTrendRangeDays(30)}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                trendRangeDays === 30 ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              30일
            </button>
          </div>
        )}
      >
          {chartRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              아직 일별 롤업 데이터가 없습니다. 스케줄이 돌면 그래프가 채워집니다.
            </p>
          ) : (
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">비즈니스 지표</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
                  {TREND_BUSINESS_ORDER.map((key) => (
                    <label key={key} className="inline-flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="rounded border-input"
                        checked={trendBizKeys.includes(key)}
                        onChange={() => toggleTrendBizKey(key)}
                      />
                      <span
                        className="font-medium"
                        style={{ borderBottom: `2px solid ${TREND_LINE_COLORS[key] || '#888'}` }}
                      >
                        {TREND_SERIES_LABELS[key] || key}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="h-72 w-full min-h-[220px]">
                  {trendBizKeys.length === 0 ? (
                    <p className="text-sm text-muted-foreground">표시할 지표를 한 개 이상 선택해 주세요.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartRows}
                        margin={{ top: 8, right: showRightAxisBiz ? 14 : 8, left: 0, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#d4d4d8" opacity={0.85} />
                        <XAxis
                          dataKey="statDate"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) =>
                            typeof v === 'string' && v.length >= 10 ? `${v.slice(5, 7)}/${v.slice(8, 10)}` : String(v)
                          }
                          minTickGap={20}
                        />
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 11 }}
                          width={48}
                          tickFormatter={(v) => formatNum(v)}
                        />
                        {showRightAxisBiz ? (
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 11 }}
                            width={48}
                            tickFormatter={(v) => formatNum(v)}
                          />
                        ) : null}
                        <Tooltip
                          contentStyle={{ borderRadius: 8, fontSize: 12 }}
                          labelFormatter={(label) =>
                            typeof label === 'string' && label.length >= 10 ? label : String(label)
                          }
                          formatter={(value, name) => [formatNum(value), TREND_SERIES_LABELS[name] || name]}
                        />
                        {trendBizKeys.map((key) => (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            name={key}
                            stroke={TREND_LINE_COLORS[key] || '#888'}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                            yAxisId={key === 'membersActiveStock' && showRightAxisBiz ? 'right' : 'left'}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="space-y-3 border-t pt-8">
                <h3 className="text-sm font-semibold text-foreground">페이지뷰(제품·채널)</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
                  {TREND_PV_KEYS.map((key) => (
                    <label key={key} className="inline-flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="rounded border-input"
                        checked={trendPvKeys.includes(key)}
                        onChange={() => toggleTrendPvKey(key)}
                      />
                      <span
                        className="font-medium"
                        style={{ borderBottom: `2px solid ${TREND_LINE_COLORS[key] || '#888'}` }}
                      >
                        {TREND_SERIES_LABELS[key] || key}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="h-72 w-full min-h-[220px]">
                  {trendPvKeys.length === 0 ? (
                    <p className="text-sm text-muted-foreground">표시할 지표를 한 개 이상 선택해 주세요.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#d4d4d8" opacity={0.85} />
                        <XAxis
                          dataKey="statDate"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) =>
                            typeof v === 'string' && v.length >= 10 ? `${v.slice(5, 7)}/${v.slice(8, 10)}` : String(v)
                          }
                          minTickGap={20}
                        />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => formatNum(v)} />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, fontSize: 12 }}
                          labelFormatter={(label) =>
                            typeof label === 'string' && label.length >= 10 ? label : String(label)
                          }
                          formatter={(value, name) => [formatNum(value), TREND_SERIES_LABELS[name] || name]}
                        />
                        {trendPvKeys.map((key) => (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            name={key}
                            stroke={TREND_LINE_COLORS[key] || '#888'}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                            yAxisId="left"
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </>
          )}
      </OpsCollapsibleCard>

      <OpsCollapsibleCard
        title={`메뉴별 클릭 수 · ${periodLabel}`}
        description={(
          <>
            웹·앱 페이지뷰를 메뉴 단위로 묶은 순위입니다. 채널은 수집 차원 기준입니다(PC=웹 데스크톱, 웹 모바일=모바일 브라우저,
            Android/iOS=네이티브 앱). 과금/구독·데이터·운영 등 <strong>운영자 전용 메뉴</strong>는 집계 대상에서 빠지므로{' '}
            <strong>표에도 나오지 않고</strong>, 점유율 분모(제품 페이지뷰 합)에도 포함되지 않습니다.
          </>
        )}
        collapsed={collapsedSections.menuClicks}
        onToggle={() => toggleSection('menuClicks')}
        contentClassName="overflow-x-auto"
      >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[7rem]">메뉴</TableHead>
                <TableHead className="text-right tabular-nums">전체</TableHead>
                <TableHead className="text-right tabular-nums">PC</TableHead>
                <TableHead className="text-right tabular-nums">웹 모바일</TableHead>
                <TableHead className="text-right tabular-nums">웹(미상)</TableHead>
                <TableHead className="text-right tabular-nums">Android</TableHead>
                <TableHead className="text-right tabular-nums">iOS</TableHead>
                <TableHead className="text-right tabular-nums">앱(미상)</TableHead>
                <TableHead className="text-right tabular-nums">기타</TableHead>
                <TableHead className="text-right tabular-nums">점유율</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!menuRows || menuRows.length === 0) && (
                <TableRow>
                  <TableCell colSpan={10} className="text-muted-foreground">
                    데이터 없음
                  </TableCell>
                </TableRow>
              )}
              {(menuRows || []).map((row, idx) => (
                <TableRow key={`${row.menuKey}-${idx}`}>
                  <TableCell className="font-medium">{menuLabel(row.menuKey)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatNum(row.clicks)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksWebDesktop'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksWebMobile'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksWebUnknown'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksAppAndroid'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksAppIos'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksAppUnknown'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNum(menuPvNum(row, 'clicksOther'))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatMenuSharePct(row.clicks, menuShareDenominator)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </OpsCollapsibleCard>
    </div>
  )
}
